import { Link, router } from "@inertiajs/react"
import { Check, ChevronLeft, ChevronRight, Languages, LogOut, Moon, Sun, User } from "lucide-react"
import { useEffect, useState, type RefObject } from "react"
import { ShellMenu, ShellMenuSeparator, shellMenuItemClass } from "@/components/shell/shell-menu"
import { useTranslation } from "@/hooks/use-translation"
import type { Locale } from "@/i18n"
import { confirm } from "@/lib/confirm"
import { saveLocalePreference, saveThemePreference } from "@/lib/preferences"
import { cn } from "@/lib/utils"
import { useUiStore } from "@/stores/ui-store"

const MENU_LOCALES: readonly Locale[] = ["ru", "en"]

export type UserMenuProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  anchorRef: RefObject<HTMLElement | null>
  rootRef: RefObject<HTMLElement | null>
  onAction: () => void
}

export function UserMenu({ open, onOpenChange, anchorRef, rootRef, onAction }: UserMenuProps) {
  const { t, locale } = useTranslation("layout")
  const theme = useUiStore((s) => s.theme)
  const [view, setView] = useState<"root" | "language">("root")

  useEffect(() => {
    if (!open) setView("root")
  }, [open])

  const close = () => onOpenChange(false)
  const localeLabel = (value: Locale) =>
    value === "en" ? t("shell.userMenu.languageEn") : t("shell.userMenu.languageRu")

  const logout = async () => {
    close()
    if (await confirm({ message: t("shell.sidebar.logoutConfirm"), confirmLabel: t("shell.sidebar.logout") })) {
      onAction()
      router.delete("/logout")
    }
  }

  const chooseLocale = (next: Locale) => {
    close()
    onAction()
    if (next !== locale) saveLocalePreference(next)
  }

  return (
    <ShellMenu
      open={open}
      onClose={close}
      anchorRef={anchorRef}
      rootRef={rootRef}
      width={232}
      direction="up"
      align="left"
      ariaLabel={t("shell.userMenu.open")}
      positionKey={view}
      onEscape={() => {
        if (view === "root") return false
        setView("root")
        return true
      }}
    >
      {view === "root" ? (
        <>
          <Link
            href="/account/profile"
            role="menuitem"
            data-shell-menu-item
            className={shellMenuItemClass}
            onClick={() => {
              close()
              onAction()
            }}
          >
            <User className="h-4 w-4 shrink-0 text-ui-text-muted" />
            {t("shell.userMenu.profile")}
          </Link>
          <button
            type="button"
            role="menuitem"
            aria-haspopup="menu"
            data-shell-menu-item
            className={shellMenuItemClass}
            onClick={() => setView("language")}
          >
            <Languages className="h-4 w-4 shrink-0 text-ui-text-muted" />
            <span className="min-w-0 flex-1">{t("shell.userMenu.language")}</span>
            <span className="text-ui-text-muted">{localeLabel(locale)}</span>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ui-text-muted" />
          </button>
          <button
            type="button"
            role="menuitem"
            data-shell-menu-item
            className={shellMenuItemClass}
            onClick={() => saveThemePreference(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? (
              <Moon className="h-4 w-4 shrink-0 text-ui-text-muted" />
            ) : (
              <Sun className="h-4 w-4 shrink-0 text-ui-text-muted" />
            )}
            <span className="min-w-0 flex-1">{t("shell.userMenu.theme")}</span>
            <span className="text-ui-text-muted">
              {theme === "dark" ? t("shell.userMenu.themeDark") : t("shell.userMenu.themeLight")}
            </span>
          </button>
          <ShellMenuSeparator />
          <button
            type="button"
            role="menuitem"
            data-shell-menu-item
            className={cn(shellMenuItemClass, "text-ui-status-failed light:text-ui-danger")}
            onClick={() => void logout()}
          >
            <LogOut className="h-3.5 w-3.5 shrink-0" />
            {t("shell.sidebar.logout")}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            role="menuitem"
            data-shell-menu-item
            className={cn(shellMenuItemClass, "text-ui-text-secondary")}
            onClick={() => setView("root")}
          >
            <ChevronLeft className="h-3.5 w-3.5 shrink-0" />
            {t("shell.userMenu.back")}
          </button>
          <ShellMenuSeparator />
          {MENU_LOCALES.map((value) => (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={value === locale}
              data-shell-menu-item
              className={shellMenuItemClass}
              onClick={() => chooseLocale(value)}
            >
              <span className="min-w-0 flex-1">{localeLabel(value)}</span>
              {value === locale ? <Check className="h-3.5 w-3.5 shrink-0 text-ui-accent-text" /> : null}
            </button>
          ))}
        </>
      )}
    </ShellMenu>
  )
}
